/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';
import { ApprovalsLocalization } from '../../../localization';
import { LogCategory } from '../../../logger';
import { ILogger, ScenarioName, StepName } from '../../../logger/interfaces';
import { StatusName } from '../../../logger/ScenarioLogger';
import { getAppHost } from '../../../utilities/TeamsUtils';

interface ErrorBoundaryProps {
    children?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    private logger: ILogger;
    private loc: ApprovalsLocalization;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.logger = getAppHost().getLogger();
        this.loc = getAppHost().getLocale();
        this.state = { hasError: false };
    }

    public static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    public componentDidCatch(error: Error, _errorInfo: React.ErrorInfo): void {
        const message = `App execution failed: ${error.name} ${error.message} at ${_errorInfo.componentStack}`;
        this.logger.logTraceError(LogCategory.AppRunning, message);
        this.logger.markScenario(ScenarioName.AppRunning, StepName.AppExecutionFailedWithErrorBoundary, StatusName.NA);
        this.logger.failScenario(ScenarioName.AppRunning, {
            data: this.getLogData(error, _errorInfo),
        });
    }

    private getLogData(error: Error, _errorInfo: React.ErrorInfo): any {
        return {
            errorName: error.name,
            errorMessage: error.message,
            errorComponentStack: _errorInfo.componentStack,
        };
    }

    public render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div>
                    <div>{this.loc.strings.error_default}</div>
                    <div>{`SessionId: ${this.logger.sessionId}`}</div>
                </div>
            );
        }
        return this.props.children;
    }
}
